import { Inject, Injectable } from '@angular/core';
import { HttpContextToken, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { APP_CONFIG, AppConfig } from '../config';
import { LanguageService } from '../language.service';

/**
 * Catalog controllers whose generic GET routes (root, `/{id}`, `/pages`) take the `lang` parameter
 * (`@Parameter(name = "lang")` in backendcommon).
 */
const LANG_PARAM_CRUD_PATHS = [
  '/new/brands',
  '/new/categories',
  '/new/characteristics',
  '/new/product-configurations',
  '/new/products',
  '/new/products/characteristics',
];

/**
 * The other GET endpoints that take the `lang` parameter (`@RequestParam("lang")` in backendcommon and
 * the guichet controllers), plus the order details: its controller doesn't declare `lang`, but its mapper
 * (ClientOrderGuichetMapper) reads it through CatalogLanguage for the products, and the order bill (PDF in
 * the client's language), the towns of a wilaya (TownMapper fills `label`/`wilayaLabel`), and the client's
 * addresses (sent ahead: AddressTO has no localized field yet). `{…}` is a path variable.
 */
const LANG_PARAM_PATHS = [
  '/new/addresses/client/{clientId}',
  '/new/brands/categories',
  '/new/carts/current-client',
  '/new/carts/guichet/current-client',
  '/new/categories/{id}/sub-categories',
  '/new/categories/activated',
  '/new/categories/configuration-category/{configurationId}',
  '/new/categories/find',
  '/new/categories/subCategory',
  '/new/favorites/client/{clientId}',
  '/new/favorites/current-client',
  '/new/orders/downloadOrderBill/{idOrder}',
  '/new/orders/guichet/client/{id}',
  '/new/product-configurations/accessory',
  '/new/product-configurations/best-rated',
  '/new/product-configurations/bestPromotion',
  '/new/product-configurations/find-by-active',
  '/new/product-configurations/find-by-state',
  '/new/product-configurations/guichet/bestPromotion',
  '/new/product-configurations/guichet/new',
  '/new/product-configurations/guichet/relatedConfigurations/{categoryId}',
  '/new/product-configurations/guichet/search-activated',
  '/new/product-configurations/guichet/search/{name}',
  '/new/product-configurations/guichet/ventFLASH',
  '/new/product-configurations/name',
  '/new/product-configurations/new',
  '/new/product-configurations/product/{productId}',
  '/new/product-configurations/relatedConfigurations/{categoryId}',
  '/new/product-configurations/search',
  '/new/product-configurations/search-activated',
  '/new/product-configurations/search-sorted',
  '/new/product-configurations/search/{name}',
  '/new/product-configurations/searchAll',
  '/new/product-configurations/sorted',
  '/new/product-configurations/sorted-by-state',
  '/new/product-configurations/ventFLASH',
  '/new/products/category/{categoryId}',
  '/new/products/characteristics/productId/{id}',
  '/new/products/dates',
  '/new/products/guichet/{productId}',
  '/new/products/old/search',
  '/new/products/search',
  '/new/town/wilaya',
];

/** Set on a request to send it without the `lang` query parameter (the header search bar) */
export const SKIP_LANG_PARAM = new HttpContextToken<boolean>(() => false);

const escapeRegExp = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Whole-path patterns of the endpoints above: `{…}` matches one path segment */
const LANG_PARAM_PATTERNS: RegExp[] = [
  ...LANG_PARAM_CRUD_PATHS.map((path) => new RegExp(`^${escapeRegExp(path)}(/pages|/\\d+)?$`)),
  ...LANG_PARAM_PATHS.map(
    (path) => new RegExp(`^${path.split(/\{[^}]+\}/).map(escapeRegExp).join('[^/]+')}$`),
  ),
];

/**
 * Sends the storefront language with the calls to our API, so the backend can answer with localized data:
 * - `lang` query parameter (fr | ar), only to the GET endpoints that take it (LANG_PARAM_CRUD_PATHS, LANG_PARAM_PATHS);
 * - `Accept-Language` header on every call, for Spring's locale resolver (error messages).
 * Requests to other hosts (S3 pre-signed URLs, Google…) are left untouched.
 */
@Injectable()
export class LanguageInterceptor implements HttpInterceptor {
  constructor(
    private languageService: LanguageService,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.isApiRequest(request.url) || this.hasLangInUrl(request.url)) {
      return next.handle(request);
    }
    const lang = this.languageService.current;
    const addLangParam =
      request.method === 'GET' &&
      !request.context.get(SKIP_LANG_PARAM) &&
      this.readsLangParam(request.url) &&
      !request.params.has('lang');
    return next.handle(
      request.clone({
        params: addLangParam ? request.params.set('lang', lang) : request.params,
        setHeaders: { 'Accept-Language': lang },
      }),
    );
  }

  private isApiRequest(url: string): boolean {
    return url.startsWith(this.appConfig.apiEndpoint);
  }

  private readsLangParam(url: string): boolean {
    const path = url.slice(this.appConfig.apiEndpoint.length).split('?')[0];
    return LANG_PARAM_PATTERNS.some((pattern) => pattern.test(path));
  }

  private hasLangInUrl(url: string): boolean {
    return /[?&]lang=/.test(url);
  }
}
